import { PhoneInput } from '@/components/blocks/inputs/PhoneInput';
import { filterFieldErrors } from '../utils';
import { Field, FieldContent, FieldError, FieldLabel } from '@/components/ui/field';
import { Skeleton } from '@/components/ui/skeleton';
import { useFieldContext } from '@/lib/form-context';
import { type VariantProps } from 'class-variance-authority';
import {
  CountryCode,
  E164Number,
  parsePhoneNumberFromString,
  PhoneNumber,
} from 'libphonenumber-js';
import { useEffect, useState } from 'react';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import { schemaFieldVariants } from '../constants';

interface TextFieldProps extends VariantProps<typeof schemaFieldVariants> {
  label: string;
  placeholder?: string;
  readOnly?: boolean;
  loading?: boolean;
  disabled?: boolean;
  required?: boolean;
}

function normalizePhone(nationalNumber: string, countryCode: CountryCode) {
  const parsed = parsePhoneNumberFromString(nationalNumber, countryCode);

  if (!parsed || !parsed.isValid()) return null;

  return {
    e164: parsed.number,
    national: parsed.nationalNumber,
    country: parsed.country,
  };
}

export const PhoneField = ({
  label,
  placeholder,
  readOnly,
  loading,
  disabled,
  required,
  variant,
}: TextFieldProps) => {
  const resolvedVariant = variant ?? 'inline';
  const [e164Value, setE164Value] = useState<string>('');
  const [isFocused, setIsFocused] = useState(false);

  const field = useFieldContext<string | undefined>();
  const form = field.form;

  const defaultCountryCode = 'US';
  const countryFieldName = `${field.name}_country_code`;

  const errors = field.state.meta.errors;
  const hasError = !isFocused && field.state.meta.isTouched && errors.length > 0;
  const isEmpty = !field.state.value || field.state.value === '';
  const displayErrors =
    hasError && required && isEmpty
      ? [{ message: `${label} is required` }]
      : filterFieldErrors(errors);

  useEffect(() => {
    if (isFocused) return;
    const initial = field.state.value;
    if (!initial) {
      setE164Value('');
      return;
    }
    const countryCode = (form.getFieldValue(countryFieldName) as string) ?? defaultCountryCode;
    const normalized = normalizePhone(initial, countryCode as CountryCode);
    if (normalized) {
      setE164Value(normalized.e164);
    }
  }, [field.state.value, isFocused]);

  return (
    <Field
      orientation={resolvedVariant === 'stacked' ? 'vertical' : 'subgrid'}
      data-invalid={hasError || undefined}
    >
      <FieldLabel htmlFor={field.name}>{label}</FieldLabel>

      <FieldContent>
        {loading ? (
          <Skeleton className="h-4 w-full" />
        ) : readOnly ? (
          <p>{e164Value ? formatPhoneNumberIntl(e164Value) : '—'}</p>
        ) : (
          <PhoneInput
            id={field.name}
            value={e164Value}
            placeholder={placeholder}
            disabled={disabled}
            international={false}
            defaultCountry={defaultCountryCode}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              setIsFocused(false);
              field.handleBlur();
            }}
            onChange={(value: E164Number | undefined) => {
              if (!value) {
                setE164Value('');
                field.handleChange(undefined);
                form.setFieldValue(countryFieldName, undefined);
                return;
              }

              setE164Value(value);

              const phoneNumber: PhoneNumber | undefined = parsePhoneNumberFromString(value);

              if (phoneNumber) {
                field.handleChange(phoneNumber.nationalNumber);
                if (phoneNumber.country !== undefined) {
                  form.setFieldValue(countryFieldName, phoneNumber.country);
                }
              }
            }}
          />
        )}
        {hasError && <FieldError errors={displayErrors} />}
      </FieldContent>
    </Field>
  );
};
